<template lang="pug">
div
  //- Title
  .d-flex.justify-space-between
    div.d-flex.align-center
      v-icon.mr-3(size="18") $yzi-file-06
      .text-md.font-semibold {{ $t('documents') }}
    v-hover(v-slot="{ hover }")
      .text-sm.font-semibold.underline.clickable.px-3.py-1.round-lg(:class="[{ 'neutral300':hover }]" @click="onEdit") {{ $t('edit') }}
  v-divider.mt-2.mb-4.neutral500
  //- Content
  v-card.round-lg.pa-4(outlined)
    //- Top Tabs
    v-row(no-gutters)
      v-col(cols="8")
        v-tabs(color="basic700" :height="44" show-arrows active-class="font-semibold")
          v-tab.text-sm.font-medium.text-none.px-4 {{ $t('all') }}
      v-col(align="end")
        .d-flex.align-center
          v-spacer
          v-btn(@click="clickSearch" icon color="neutral500")
            v-icon(size="20") $yzi-search-sm
          v-text-field(v-if="showSearchField" v-model="searchText" @blur="blurSearch" solo dense flat placeholder="Type to search..." hide-details clearable autofocus)
    v-divider
    //- Data
    .data-table
      v-data-table.mt-sm-4.pb-6(:headers="headers" :items="items" item-key="id" :items-per-page="pageSize" :page="currentPage" :search="searchText" hide-default-footer  :header-props="{ sortIcon: '$yzi-triangle' }" loader-height="2")
        //- actions
        template(v-slot:item.actions="{ item, index }")
          .d-flex.justify-end
            v-tooltip(top color="black" content-class="black")
              template(#activator="{ on, attrs }")
                v-btn.mr-1(v-bind="attrs" v-on="on" icon small @click="onDownload(item)")
                  v-icon(size=20) $yzi-download-01
              .text-sm.font-semibold.basic500--text {{ $t('download') }}
            v-menu(offset-y left rounded="lg")
              template(v-slot:activator="{ on }")
                v-btn(icon small v-on="on")
                  v-icon mdi-dots-horizontal
              v-list.py-0
                v-list-item(@click="onDeleteItem(item, index)")
                  v-list-item-title.text-sm.danger500--text {{ $t('delete') }}
      div.d-flex.align-center(v-if="totalCount > pageSize")
        v-spacer
        div
          yc-pagination-custom(v-model="currentPage" :parentPageSize.sync="pageSize" :count="totalCount" text="Item list")
  .dialogs
    delete-confirmation-dialog(v-if="openDeleteConfirmationDialog" :open.sync="openDeleteConfirmationDialog" :loading="isDeletingItem" @cancel="onCancelDeleteItem" @delete="onConfirmDeleteItem")
      delete-confirmation-details
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import DeleteConfirmationDialog from '@/components/clinic/dialogs/DeleteConfirmationDialog.vue'
import DeleteConfirmationDetails from '@/components/clinic/invoice/DeleteConfirmationDetails.vue'

export default {
  components: {
    DeleteConfirmationDialog, DeleteConfirmationDetails
  },
  props: {
    patientId: {
      type: Number,
      required: true
    },
    leadPid: {
      type: String,
      required: true
    },
    items: {
      type: Array,
      default: () => {
        return []
      }
    },
    page: {
      type: Number,
      default: null
    },
    source: {
      type: String,
      default: 'patient-profile'
    }
  },
  data() {
    return {
      searchText: null,
      showSearchField: false,
      currentPage: 1,
      pageSize: 10,
      headers: [
        {
          text: 'name',
          value: 'name',
          sortable: true
        },
        {
          text: '',
          value: 'actions',
          sortable: false
        }
      ],
      selectedItem: null,
      selectedIndex: null,
      openDeleteConfirmationDialog: false,
      isDeletingItem: false
    }
  },
  computed: {
    ...mapState('user', ['user']),
    ...mapGetters({
      company: 'getCompany'
    }),
    totalCount() {
      return this.items.length
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    clickSearch () {
      if (this.showSearchField) {
        if (!this.searchText || this.searchText.length === 0) {
          this.showSearchField = false
        }
      } else {
        this.showSearchField = true 
      }
    },
    blurSearch () {
      if (!this.searchText || this.searchText.length === 0) {
        this.showSearchField = false
      }
    },
    async onDownload (item) {
      let key
      try {
        const { data } = await this.$axios.post(`users/${this.user.id}/request_otk`)
        key = data.key
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        return
      }
      let baseUrl = process.env.VUE_APP_BASE_URL
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        baseUrl = 'https://api-test.yezza.io'
      } else if (process.env.VUE_APP_ENV === 'prod') {
        baseUrl = 'https://export.yezza.io'
      }
      const url = `${baseUrl}/clinics/${this.company.slug}/registrations/${this.leadPid}/preview_document?leadproduct=${item.id}&key=${key}`
      window.open(url,'_blank')
    },
    onDeleteItem (item, index) {
      this.selectedItem = item
      this.selectedIndex = index
      this.openDeleteConfirmationDialog = true
    },
    onCancelDeleteItem () {
      this.selectedItem = null
      this.selectedIndex = null
      this.openDeleteConfirmationDialog = false
    },
    async onConfirmDeleteItem () {
      try {
        this.isDeletingItem = true
        const id = this.selectedItem.id
        await this.$axios.delete(`/leads/${this.leadPid}/products/${id}`)
        this.items.splice(this.selectedIndex, 1)
        this.showMessage(['success', this.$t('success_message.successfully_updated')])
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isDeletingItem = false
        this.openDeleteConfirmationDialog = false
      }
    },
    onEdit () {
      if (this.source === 'patient-history') {
        // this.$router.push(`/admin/clinic/invoice/${this.leadPid}?origin=patient-history&page=${this.page}&leadPid=${this.leadPid}`)
        let params = { pid: this.leadPid, origin: 'ClinicConsultationDetails', data: { params: { pid: this.leadPid, savePage: this.page, viewAllHistory: true } } }
        if (this.$route.params && this.$route.params.origin && this.$route.params.data) {
          params.data.params.route = { name: this.$route.params.origin, ...this.$route.params.data }
        }
        this.$router.push({ name: 'ClinicInvoice', params })
      } else {
        // this.$router.push(`/admin/clinic/invoice/${this.leadPid}?origin=patient-profile&patientId=${this.patientId}&leadPid=${this.leadPid}`)
        let params = { pid: this.leadPid, origin: 'ClinicPatientDetails', data: { params: { id: this.patientId }, query: { leadPid: this.leadPid, tab: 'visits' } } }
        this.$router.push({ name: 'ClinicInvoice', params })
      }
    }
  },
}
</script>

<style lang="scss" scoped>
</style>